import _ from "underscore";

import useQueriedRecords from "api/api-hooks/records/useQueriedRecords";
import useFieldMap from "hooks/useFieldMap";
import { useMemo, useRef } from "react";
import { currencyToReadable } from "utils/DashboardUtils";

const useAggregationData = ({
  moduleAPIName,
  query,
  target,
  validator,
  topLevelFilters,
  viewFilters,
  record,
}) => {
  const fieldTypeMap = useFieldMap(moduleAPIName, "FieldType");
  const variableValues = useMemo(() => {
    let vals = {};
    if (record) {
      vals["record"] = record;
    }

    return vals;
  }, [record]);

  const { records, recordsLoading } = useQueriedRecords({
    moduleAPIName: moduleAPIName,
    query: query,
    topLevelFilters: topLevelFilters,
    viewFilters: viewFilters,
    queryValidator: (query) => validator(query),
    variableValues: variableValues,
  });
  const recordsRef = useRef();
  recordsRef.current = records;

  const aggregationField = query?.Aggregations?.[0]?.Field;
  const aggregationFieldType = fieldTypeMap[aggregationField];
  const aggregationMethod = query?.Aggregations?.[0]?.Method;
  const stringifiedRecords = JSON.stringify(records);

  const getMention = (originalString) => {
    if (!record || !originalString) return originalString;

    try {
      _.templateSettings = {
        interpolate: /\{\{(.+?)\}\}/g,
      };
      const compiledTemplate = _.template(originalString);
      const compTemplate = compiledTemplate(record);
      return compTemplate.includes("function") ? null : compTemplate;
    } catch {
      return originalString.replace(/{{\s*[^{}]+\s*}}/g, "");
    }
  };

  const data = useMemo(() => {
    let records = recordsRef.current;
    let result;
    let readable;

    if (!records || !records?.length) return [];

    let aggregationResult = records[0]?.[aggregationMethod]?.[aggregationField];

    // Handle Currency/CurrencyInterval cases
    if (typeof aggregationResult === "object") {
      readable = currencyToReadable(aggregationResult);
      aggregationResult = aggregationResult?.Value;
    }

    result = {
      field: aggregationField,
      fieldType: aggregationFieldType,
      method: aggregationMethod,
      value: aggregationResult,
      readable: readable ? readable : aggregationResult,
    };

    // Exit early if there is no target provided
    if (!target) return result;

    // If there is a target, we need to calculate the percentage
    let targetNumber;
    let targetReadable;

    // Add any special FieldType handling here
    if (
      aggregationFieldType === "Currency" ||
      aggregationFieldType === "CurrencyInterval"
    ) {
      targetNumber = target.Value;
      targetReadable = currencyToReadable(target);
    }

    if (typeof target === "string" && target.includes("{{")) {
      targetNumber = getMention(target);
    } else {
      targetNumber = parseFloat(target);
    }

    const percent = aggregationResult / targetNumber;
    const remainingPercent = 1 - percent;
    const finalResult = [
      { type: "Completed", percent: percent },
      {
        type: "To be completed",
        percent: remainingPercent > 0 ? remainingPercent : 0, // Prevent negative values
      },
    ];
    result.percent = (percent * 100).toFixed(2) + "%"; // Readable percentage
    result.result = finalResult;
    result.target = targetNumber;
    result.targetReadable = targetReadable ? targetReadable : targetNumber;
    result.field = aggregationField;
    result.fieldType = aggregationFieldType;

    return result;
  }, [
    stringifiedRecords,
    aggregationField,
    aggregationFieldType,
    aggregationMethod,
    target,
  ]);

  return {
    chartData: data,
    chartError: null,
    chartLoading: recordsLoading,
    aggregationField,
    aggregationMethod,
  };
};

export default useAggregationData;
